import { z } from 'zod';
import { DIET_TYPES } from '@/types';

// Enhanced Diet Type Validations
const allergenInfoSchema = z.object({
  nuts: z.boolean().default(false),
  dairy: z.boolean().default(false),
  gluten: z.boolean().default(false),
  soy: z.boolean().default(false),
  eggs: z.boolean().default(false),
  shellfish: z.boolean().default(false),
  sesame: z.boolean().default(false),
  mustard: z.boolean().default(false),
  custom: z.array(z.string()).default([]),
  mayContain: z.array(z.string()).default([]),
});

const spiceLevelSchema = z.object({
  level: z.enum(['none', 'mild', 'medium', 'hot', 'extra-hot']).default('none'),
  customDescription: z.string().optional(),
  preparationNotes: z.string().optional(),
});

const cuisineTagSchema = z.object({
  type: z.enum(['north-indian', 'south-indian', 'chinese', 'continental', 'street-food', 'desserts', 'beverages']),
  region: z.string().optional(),
  subType: z.string().optional(),
});

const customDietaryTagSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Tag name is required'),
  description: z.string().optional(),
  category: z.enum(['ingredient', 'preparation', 'nutritional', 'custom']),
  color: z.string().optional(),
});

// Menu Section Validation
export const menuSectionSchema = z.object({
  name: z
    .string()
    .min(1, 'Section name is required')
    .max(100, 'Section name must be less than 100 characters')
    .trim(),
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .trim()
    .optional()
    .or(z.literal('')),
});

export const menuSectionUpdateSchema = menuSectionSchema.partial();

// Menu Item Validation  
export const menuItemSchema = z.object({
  name: z
    .string()
    .min(1, 'Item name is required')
    .max(200, 'Item name must be less than 200 characters')
    .trim(),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .trim()
    .optional()
    .or(z.literal('')),
  price: z
    .string()
    .min(1, 'Price is required')
    .refine(
      (val) => {
        const num = parseFloat(val);
        return !isNaN(num) && num >= 0 && num <= 10000;
      },
      'Price must be a valid number between 0 and 10,000'
    ),
  dietType: z.enum(['veg', 'non-veg', 'contains-eggs'], {
    required_error: 'Diet type is required',
  }),
  sectionId: z.string().min(1, 'Section is required'),
  ingredients: z
    .string()
    .max(500, 'Ingredients must be less than 500 characters')
    .trim()
    .optional()
    .or(z.literal('')),
  allergens: z
    .string()
    .max(500, 'Allergens must be less than 500 characters')
    .trim()
    .optional()
    .or(z.literal('')),
  spiceLevel: z
    .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)])
    .optional(),
  preparationTime: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        const num = parseInt(val);
        return !isNaN(num) && num > 0 && num <= 180;
      },
      'Preparation time must be between 1 and 180 minutes'
    ),
  calories: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        const num = parseInt(val);
        return !isNaN(num) && num > 0 && num <= 5000;
      },
      'Calories must be between 1 and 5000'
    ),
  isVegan: z.boolean().optional(),
  isJain: z.boolean().optional(),
  // Enhanced diet type fields
  enhancedAllergens: allergenInfoSchema.optional(),
  enhancedSpiceLevel: spiceLevelSchema.optional(),
  cuisineTags: z.array(cuisineTagSchema).default([]),
  customTags: z.array(customDietaryTagSchema).default([]),
  isSignatureDish: z.boolean().default(false),
  isChefSpecial: z.boolean().default(false),
});

export const menuItemUpdateSchema = menuItemSchema.partial();

// Form Data Types
export type MenuSectionFormData = z.infer<typeof menuSectionSchema>;
export type MenuItemFormData = z.infer<typeof menuItemSchema>;

// Validation Helper Functions
export const validateMenuSectionName = (name: string): string | undefined => {
  const result = z.string().min(1).max(100).safeParse(name.trim());
  return result.success ? undefined : result.error.issues[0]?.message;
};

export const validatePrice = (price: string): string | undefined => {
  if (!price || price.trim() === '') return 'Price is required';
  
  const num = parseFloat(price);
  if (isNaN(num)) return 'Price must be a valid number';
  if (num < 0) return 'Price must be 0 or greater';
  if (num > 10000) return 'Price must be less than 10,000';
  
  return undefined;
};

export const validateDietType = (dietType: string): string | undefined => {
  if (!dietType) return 'Diet type is required';
  if (!Object.keys(DIET_TYPES).includes(dietType)) {
    return 'Invalid diet type';
  }
  return undefined;
};

export const validatePreparationTime = (time: string): string | undefined => {
  if (!time || time === '') return undefined;
  
  const num = parseInt(time);
  if (isNaN(num)) return 'Preparation time must be a valid number';
  if (num <= 0) return 'Preparation time must be greater than 0';
  if (num > 180) return 'Preparation time must be less than 180 minutes';
  
  return undefined;
};

export const validateCalories = (calories: string): string | undefined => {
  if (!calories || calories === '') return undefined;
  
  const num = parseInt(calories);
  if (isNaN(num)) return 'Calories must be a valid number';
  if (num <= 0) return 'Calories must be greater than 0';
  if (num > 5000) return 'Calories must be less than 5000';
  
  return undefined;
};